import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, FileText, Layers, ClipboardList, Phone, Users,
  BarChart3, Settings, LogOut, Sun, Moon, ChevronLeft, ShieldCheck,
} from 'lucide-react';
import api, { clearStoredAuthTokens } from '../../services/axios';
import { redirectToLogin } from '../../utils/authSession';
import { useAuth } from '../../context/AuthContext';
import SettingsModal from './SettingsModal';
import { openWizetokInNewTab } from '../../utils/crossAppSso';
import { isFeatureEnabled, isProductEnabled } from '../../utils/entitlements';
import { canAccessNav, profileRoleLabel, sessionCompanyKind } from '../../utils/roleAccess';

const BLUE = '#0041C2';
const GRAD = 'linear-gradient(135deg,#0041C2,#1A5FD4)';
const GRAD2 = 'linear-gradient(90deg,#0041C2,#1A5FD4)';

const NAV_CONFIG = [
  { type: 'section', label: 'Overview' },
  {
    type: 'item', label: 'Dashboard', tip: 'Dashboard',
    paths: ['/analytics'], icon: LayoutGrid,
    access: 'dashboard',
  },
  { type: 'section', label: 'Tools' },
  {
    type: 'item', label: 'Craft JD', tip: 'Craft JD',
    paths: ['/open-positions', '/jd-preview', '/database'], icon: FileText,
    // Positions/JDs are the base of ReachOut, so Craft JD stays open whenever ReachOut is on.
    // The jd_craft switch only turns off the AI inside it (see utils/entitlements isAiJdEnabled).
    product: 'reachout',
    access: 'tools',
  },
  {
    type: 'item', label: 'Stack Ranking', tip: 'Stack Ranking',
    paths: ['/stack-ranking', '/existing-positions'], icon: Layers,
    // Like Craft JD: the stack_ranking switch only turns off the AI ranking inside (isAiRankingEnabled).
    product: 'reachout',
    access: 'tools',
  },
  {
    type: 'item', label: 'ReachOut', tip: 'ReachOut',
    paths: ['/engagement', '/whatsapp-reachout'], icon: Phone,
    feature: 'outreach',
    access: 'tools',
  },
  {
    type: 'item', label: 'Format Resume', tip: 'Format Resume',
    paths: ['/resume-formatter'], icon: ClipboardList,
    feature: 'resume_flow',
    access: 'tools',
  },
  {
    type: 'item', label: 'Open Wizetok', tip: 'Open Wizetok (SSO)',
    paths: ['/open-wizetok'], icon: Users, external: 'wizetok',
    product: 'wizetok',
    access: 'wizetok',
  },
  { type: 'section', label: 'Workspace' },
  {
    type: 'item', label: 'Administration', tip: 'Administration',
    paths: ['/admin'], icon: ShieldCheck,
    roles: ['SUPER_ADMIN', 'TENANT_ADMIN', 'COMPANY_OWNER', 'COMPANY_ADMIN', 'COMPANY_MANAGER', 'COMPANY_USER', 'RECRUITER'],
    access: 'administration',
  },
  {
    type: 'item', label: 'Analytics', tip: 'Analytics',
    paths: ['/analytics'], icon: BarChart3,
    access: 'overview',
  },
];

function isBrowserSafePictureUrl(u) {
  return (
    typeof u === 'string' && u.trim() &&
    !u.includes('graph.microsoft.com') &&
    !u.includes('login.microsoftonline.com')
  );
}

const Sidebar = ({ collapsed, onToggleCollapse, theme, onThemeToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, tenantRole, tenantRoles, entitlements, unrestricted } = useAuth();
  const kind = sessionCompanyKind(tenantRole, role, unrestricted, tenantRoles);
  const showSettings = canAccessNav(kind, 'settings');
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState('job-family');
  const sidebarWidth = collapsed ? 64 : 230;

  useEffect(() => { setAvatarImgError(false); }, [user?.pictureUrl, user?.pictureDataUrl]);

  useEffect(() => {
    const onOpenSettings = (e) => {
      if (!canAccessNav(sessionCompanyKind(tenantRole, role, unrestricted, tenantRoles), 'settings')) return;
      setSettingsSection(e.detail?.sectionId || 'job-family');
      setSettingsOpen(true);
    };
    window.addEventListener('reachout:open-settings', onOpenSettings);
    return () => window.removeEventListener('reachout:open-settings', onOpenSettings);
  }, [role, tenantRole, unrestricted]);

  const assignedRole = profileRoleLabel(tenantRole, role, unrestricted, tenantRoles);
  const username = user?.email ? user.email.split('@')[0] : '';

  const displayName = useMemo(() => {
    if (!user) return 'Account';
    const first = (user.firstName || '').trim();
    const last = (user.lastName || '').trim();
    const full = `${first}${first && last ? ' ' : ''}${last}`.trim();
    return full || user.email || 'Account';
  }, [user]);

  const avatarInitials = useMemo(() => {
    const parts = displayName.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  }, [displayName]);

  const avatarUrlCandidate =
    user?.pictureDataUrl ||
    (isBrowserSafePictureUrl(user?.pictureUrl) ? user.pictureUrl : null);
  const avatarUrl =
    avatarUrlCandidate &&
    typeof avatarUrlCandidate === 'string' &&
    avatarUrlCandidate.trim() &&
    avatarUrlCandidate.trim().toLowerCase() !== 'null'
      ? avatarUrlCandidate.trim()
      : null;

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    finally { clearStoredAuthTokens(); redirectToLogin(); }
  };

  const navTo = (path) => {
    navigate(path, { state: { navReload: Date.now() } });
  };

  const isActive = (paths) =>
    paths.some((p) => {
      if (p === '/analytics') return location.pathname === '/analytics';
      return location.pathname === p || location.pathname.startsWith(p + '/');
    });

  const isDark = theme === 'dark';

  // Publish sidebar width so portalled modals can offset themselves to the visible area.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-sidebar-width',
      collapsed ? '64px' : '230px'
    );
  }, [collapsed]);

  return (
    <div
      style={{
        position: 'relative',
        width: sidebarWidth,
        flexShrink: 0,
        transition: 'width 0.32s cubic-bezier(0.4,0,0.2,1)',
        height: '100vh',
        zIndex: 10,
      }}
    >
    <aside
      data-role-nav="true"
      style={{
        width: '100%',
        height: '100%',
        background: '#0D1117',
        borderRight: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
      }}
    >
      {/* Blue top accent line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: GRAD2, zIndex: 2 }} />

      {/* Logo — height exactly 52 px to align with topbar border */}
      <div
        style={{
          height: 52,
          padding: '0 14px',
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : 10,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          minWidth: sidebarWidth,
          flexShrink: 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <div
          style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            background: GRAD,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 700, color: '#fff',
            boxShadow: '0 0 20px rgba(0,65,194,0.35)',
          }}
        >
          H
        </div>
        {!collapsed && (
        <div style={{ whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            feuji
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.01em' }}>
            ReachOut
          </div>
        </div>
        )}
      </div>

      {/* Theme Toggle */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          minWidth: sidebarWidth,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <span
          style={{
            fontSize: 11, fontWeight: 500, color: '#9CA3AF',
            whiteSpace: 'nowrap', flex: 1,
            display: collapsed ? 'none' : 'inline',
          }}
        >
          {isDark ? 'Dark mode' : 'Light mode'}
        </span>
        <div
          onClick={onThemeToggle}
          style={{
            width: 42, height: 23, borderRadius: 12,
            background: isDark ? 'rgba(255,255,255,0.1)' : BLUE,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : BLUE}`,
            position: 'relative', cursor: 'pointer', flexShrink: 0,
            transition: 'background 0.28s,border-color 0.28s',
          }}
        >
          <div
            style={{
              position: 'absolute', top: 2,
              left: isDark ? 2 : 21,
              width: 17, height: 17, borderRadius: '50%',
              background: isDark ? 'rgba(255,255,255,0.3)' : '#fff',
              transition: 'left 0.28s cubic-bezier(0.4,0,0.2,1),background 0.28s',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {isDark
              ? <Moon size={9} color="#9CA3AF" />
              : <Sun size={9} color={BLUE} />
            }
          </div>
        </div>
      </div>

      {/* Nav scroll area */}
      <div
        className="app-sidebar-scroll"
        style={{
          flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '10px 10px',
        }}
      >
        {NAV_CONFIG.map((item, idx) => {
          if (item.hidden) return null;
          if (
            item.external === 'wizetok'
            && !isProductEnabled(entitlements, 'wizetok', { unrestricted })
          ) {
            return null;
          }
          if (item.roles && !item.roles.includes(role) && !(tenantRoles || []).some((r) => item.roles.includes(r)) && !item.roles.includes(tenantRole) && !unrestricted) {
            return null;
          }
          if (item.type !== 'section' && !canAccessNav(kind, item.access)) {
            return null;
          }
          if (item.type === 'section') {
            let hasVisibleChild = false;
            for (let i = idx + 1; i < NAV_CONFIG.length; i += 1) {
              if (NAV_CONFIG[i].type === 'section') break;
              const child = NAV_CONFIG[i];
              if (child.hidden) continue;
              if (
                child.external === 'wizetok'
                && !isProductEnabled(entitlements, 'wizetok', { unrestricted })
              ) {
                continue;
              }
              const roleOk = !child.roles || child.roles.includes(role) || child.roles.includes(tenantRole) || (tenantRoles || []).some((r) => child.roles.includes(r)) || unrestricted;
              if (roleOk && canAccessNav(kind, child.access)) {
                hasVisibleChild = true;
                break;
              }
            }
            if (!hasVisibleChild) return null;
          }

          if (item.type === 'section') {
            return (
              <div
                key={idx}
                style={{
                  fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
                  color: '#4A5568', padding: '0 8px 5px',
                  marginTop: idx > 0 ? 14 : 0,
                  whiteSpace: 'nowrap', fontWeight: 600,
                  display: collapsed ? 'none' : 'block',
                }}
              >
                {item.label}
              </div>
            );
          }

          const Icon = item.icon;
          const active = isActive(item.paths);
          const primaryPath = item.paths[0];
          const locked = Boolean(
            (item.feature && !isFeatureEnabled(entitlements, 'reachout', item.feature, { unrestricted }))
            || (item.product && !isProductEnabled(entitlements, item.product, { unrestricted }))
          );

          if (item.disabled) {
            return (
              <NavItem
                key={idx}
                label={item.label}
                tip={item.tip}
                active={false}
                collapsed={collapsed}
                pip={false}
                onClick={undefined}
                disabled={true}
              >
                <Icon size={18} color="#4B5563" />
              </NavItem>
            );
          }

          return (
            <NavItem
              key={idx}
              label={item.label}
              tip={item.tip}
              active={active}
              collapsed={collapsed}
              pip={item.pip}
              onClick={() => {
                if (item.external === 'wizetok') {
                  if (!locked) openWizetokInNewTab();
                  return;
                }
                navTo(primaryPath);
              }}
            >
              <Icon size={18} color={active ? '#FFFFFF' : '#6B7280'} />
            </NavItem>
          );
        })}
      </div>

      {/* Bottom section */}
      <div
        style={{
          padding: '10px 10px 14px',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          minWidth: sidebarWidth,
        }}
      >
        {showSettings && (
        <BottomItem collapsed={collapsed} onClick={() => setSettingsOpen(true)} tip="Settings">
          <Settings size={18} color="#6B7280" />
          {!collapsed && (
            <span style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Settings
            </span>
          )}
        </BottomItem>
        )}

        <div
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px', borderRadius: 9,
            cursor: 'pointer', transition: 'background 0.18s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          title="Logout"
        >
          <div
            style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: GRAD,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#fff',
              boxShadow: '0 0 10px rgba(0,65,194,0.4)', overflow: 'hidden',
            }}
          >
            {avatarUrl && !avatarImgError ? (
              <img
                src={avatarUrl}
                alt={displayName}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={() => setAvatarImgError(true)}
              />
            ) : (
              avatarInitials
            )}
          </div>
          <div style={{ whiteSpace: 'nowrap', flex: 1, minWidth: 0, opacity: collapsed ? 0 : 1, transition: 'opacity 0.2s' }}>
            <div
              style={{
                fontSize: 12, fontWeight: 600, color: '#E5E7EB',
                overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </div>
            <div style={{ fontSize: 10, color: '#6B7280' }}>
              {username || user?.email || 'Account'}
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', marginTop: 1 }}>
              {assignedRole}
            </div>
          </div>
          <LogOut size={14} color="#4A5568" style={{ flexShrink: 0, opacity: 1, transition: 'opacity 0.2s' }} />
        </div>
      </div>
    </aside>

    {/* Collapse toggle — outside aside's overflow:hidden so it renders fully */}
    <button
      onClick={onToggleCollapse}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      style={{
        position: 'absolute', right: -12, top: 14,
        width: 24, height: 24, borderRadius: '50%',
        background: BLUE, border: '2px solid #FFFFFF',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', zIndex: 30,
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        outline: 'none',
      }}
    >
      <ChevronLeft
        size={10}
        color="#FFFFFF"
        style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }}
      />
    </button>

    <SettingsModal
      show={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      initialSectionId={settingsSection}
    />
  </div>
  );
};

function NavItem({ label, tip, active, collapsed, pip, onClick, disabled, children }) {
  const [hovered, setHovered] = useState(false);
  const GRAD = 'linear-gradient(135deg,#0041C2,#1A5FD4)';

  return (
    <div
      onClick={disabled ? undefined : onClick}
      title={collapsed ? tip : (disabled ? 'Coming Soon' : '')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 10px', borderRadius: 9,
        cursor: disabled ? 'not-allowed' : 'pointer',
        marginBottom: 2, whiteSpace: 'nowrap', position: 'relative',
        border: `1px solid ${active ? 'rgba(255,255,255,0.08)' : 'transparent'}`,
        background: active
          ? 'rgba(255,255,255,0.1)'
          : hovered && !disabled ? 'rgba(255,255,255,0.07)' : 'transparent',
        opacity: disabled ? 0.4 : 1,
        transition: 'background 0.18s, border-color 0.18s, opacity 0.18s',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      {active && (
        <div
          style={{
            position: 'absolute', left: 0, top: '22%', bottom: '22%',
            width: 2, borderRadius: 2,
            background: GRAD,
            boxShadow: '0 0 8px rgba(0,65,194,0.7)',
          }}
        />
      )}
      {children}
      {!collapsed && (
        <span
          style={{
            fontSize: 13,
            color: active ? '#FFFFFF' : '#9CA3AF',
            fontWeight: active ? 600 : 500,
            flex: 1,
          }}
        >
          {label}
        </span>
      )}
      {disabled && !collapsed && (
        <span style={{ fontSize: 8, fontWeight: 700, color: '#6B7280', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', padding: '1px 5px', borderRadius: 6, letterSpacing: '0.04em' }}>
          SOON
        </span>
      )}
    </div>
  );
}

function BottomItem({ children, onClick, collapsed, tip }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? (tip || '') : ''}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 9,
        cursor: 'pointer',
        background: hovered ? 'rgba(255,255,255,0.07)' : 'transparent',
        transition: 'background 0.18s',
        marginBottom: 4, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </div>
  );
}

export default Sidebar;
